import { ConsentType } from '../contexts/ConsentContext';
import {
    initializeAnalytics as initGA,
    updateAnalyticsConsent as updateGA
} from './analytics';

// Storage keys that need consent
export const ESSENTIAL_STORAGE_KEYS = [
    'app__access_token',
    'app__refresh_token',
    'admin__access_token',
    'admin__refresh_token'
];

export const PREFERENCES_STORAGE_KEYS = [
    'app__mic_check',
    'app__mic_check_date'
];

// Check if a storage key requires consent
export const requiresConsent = (key: string): ConsentType | null => {
    if (ESSENTIAL_STORAGE_KEYS.includes(key)) {
        return 'essential';
    }

    if (PREFERENCES_STORAGE_KEYS.includes(key)) {
        return 'preferences';
    }

    // If the key doesn't match any known keys, assume it requires preferences consent
    if (key.startsWith('app__') || key.startsWith('admin__')) {
        return 'preferences';
    }

    return null;
};

// Check if a consent type allows indexedDB usage
export const canUseIndexedDB = (consent: { essential: boolean; preferences: boolean }): boolean => {
    // IndexedDB stores speech data, which is essential for the app functionality
    return consent.essential;
};

// Check if a consent type allows analytics
export const canUseAnalytics = (consent: { analytics: boolean }): boolean => {
    return consent.analytics;
};

// Safe localStorage getter that respects consent
export const safeGetItem = (
    key: string,
    consentState: { essential: boolean; preferences: boolean; analytics: boolean }
): string | null => {
    const consentType = requiresConsent(key);

    // If no consent required or appropriate consent given
    if (!consentType || consentState[consentType]) {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.error(`Error accessing localStorage for key ${key}:`, error);
            return null;
        }
    }

    console.warn(`Access to ${key} denied due to lack of ${consentType} consent`);
    return null;
};

// Safe localStorage setter that respects consent
export const safeSetItem = (
    key: string,
    value: string,
    consentState: { essential: boolean; preferences: boolean; analytics: boolean }
): boolean => {
    const consentType = requiresConsent(key);

    // If no consent required or appropriate consent given
    if (!consentType || consentState[consentType]) {
        try {
            localStorage.setItem(key, value);
            return true;
        } catch (error) {
            console.error(`Error writing to localStorage for key ${key}:`, error);
            return false;
        }
    }

    console.warn(`Write to ${key} denied due to lack of ${consentType} consent`);
    return false;
};

// Safe localStorage removal that respects consent
export const safeRemoveItem = (
    key: string,
    consentState: { essential: boolean; preferences: boolean; analytics: boolean }
): boolean => {
    const consentType = requiresConsent(key);

    // If no consent required or appropriate consent given
    if (!consentType || consentState[consentType]) {
        try {
            localStorage.removeItem(key);
            return true;
        } catch (error) {
            console.error(`Error removing from localStorage for key ${key}:`, error);
            return false;
        }
    }

    console.warn(`Removal of ${key} denied due to lack of ${consentType} consent`);
    return false;
};

// Functions for managing Google Analytics consent
export const initializeAnalytics = (consentState: { analytics: boolean }): void => {
    // Initialize Google Analytics via our analytics service
    initGA(consentState);
    
    // Also update Civic Cookie Control if available
    if (window.CookieControl && window.CookieControl.hasConsented) {
        const civicConsent = window.CookieControl.hasConsented();
        if (civicConsent && civicConsent.analytics) {
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'granted'
                });
            }
        }
    }
};

export const updateAnalyticsConsent = (consentState: { analytics: boolean }): void => {
    // Update Google Analytics consent via our analytics service
    updateGA(consentState);
    
    // Also update Civic Cookie Control if available
    if (window.CookieControl && window.CookieControl.hasConsented) {
        const civicConsent = window.CookieControl.hasConsented();
        if (civicConsent) {
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': civicConsent.analytics ? 'granted' : 'denied'
                });
            }
        }
    }
};

// Check if Civic Cookie Control is available and get consent status
export const getCivicConsent = (): { essential: boolean; analytics: boolean; preferences: boolean } | null => {
    if (window.CookieControl && window.CookieControl.hasConsented) {
        const consent = window.CookieControl.hasConsented();
        return {
            essential: true, // Essential is always true
            analytics: consent?.analytics || false,
            preferences: consent?.preferences || false
        };
    }
    return null;
};
